/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2023.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QCriteriaOperator} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QCriteriaOperator";
import {QFilterCriteria} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QFilterCriteria";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button/Button";
import Icon from "@mui/material/Icon/Icon";
import {GridSlotsComponentsProps} from "@mui/x-data-grid-pro";
import {GridFilterPanelProps} from "@mui/x-data-grid/components/panel/filterPanel/GridFilterPanel";
import MDTypography from "qqq/components/legacy/MDTypography";
import {FilterCriteriaRow, getDefaultCriteriaValue} from "qqq/components/query/FilterCriteriaRow";
import {QueryScreenUsage} from "qqq/pages/records/query/RecordQuery";
import React, {forwardRef, useReducer} from "react";


declare module "@mui/x-data-grid"
{
   ///////////////////////////////////////////////////////////////////
   // this lets these props be passed in via <DataGrid slotProps>   //
   ///////////////////////////////////////////////////////////////////
   interface FilterPanelPropsOverrides
   {
      tableMetaData: QTableMetaData;
      metaData: QInstance;
      queryFilter: QQueryFilter;
      updateFilter: (newFilter: QQueryFilter) => void;
      allowVariables?: boolean;
      queryScreenUsage?: QueryScreenUsage;
      omitExposedJoins?: string[]

      //////////////////////////////////////////////////////////////////////////////////
      // how many of the LEADING criteria are locked - shown read-only, not removable //
      //////////////////////////////////////////////////////////////////////////////////
      lockedCriteriaCount?: number

      /////////////////////////////////////////////////////////////////////////////////////
      // whether the filter in play says Or while the locked criteria are being enforced //
      // as And - the caption tells the user, so the And they are getting is disclosed   //
      // rather than silent                                                              //
      /////////////////////////////////////////////////////////////////////////////////////
      lockedCriteriaIsOverridingBooleanOperator?: boolean
   }
}


export class QFilterCriteriaWithId extends QFilterCriteria
{
   id: number;

   public static buildFromCriteria(criteria: QFilterCriteria, id: number)
   {
      const rs = new QFilterCriteria(criteria.fieldName, criteria.operator, criteria.values) as QFilterCriteriaWithId;
      rs.id = id;
      return (rs);
   }
}


let debounceTimeout: string | number | NodeJS.Timeout;
let criteriaId = (new Date().getTime()) + 1000;

export const CustomFilterPanel = forwardRef<any, GridFilterPanelProps>(
   function MyCustomFilterPanel(props: GridSlotsComponentsProps["filterPanel"], ref)
   {
      const [, forceUpdate] = useReducer((x) => x + 1, 0);

      const queryFilter = props.queryFilter;

      // console.log(`CustomFilterPanel: filter: ${JSON.stringify(queryFilter)}`);

      function focusLastField()
      {
         setTimeout(() =>
         {
            try
            {
               // console.log(`Try to focus ${criteriaId - 1}`);
               document.getElementById(`field-${criteriaId - 1}`).focus();
            }
            catch (e)
            {
               console.log("Error trying to focus field ...", e);
            }
         });
      }

      const addCriteria = () =>
      {
         const qFilterCriteriaWithId = new QFilterCriteriaWithId(null, QCriteriaOperator.EQUALS, getDefaultCriteriaValue());
         qFilterCriteriaWithId.id = criteriaId++;
         console.log(`adding criteria id ${qFilterCriteriaWithId.id}`);
         queryFilter.criteria.push(qFilterCriteriaWithId);
         props.updateFilter(queryFilter);
         forceUpdate();

         focusLastField();
      };

      if (!queryFilter.criteria)
      {
         queryFilter.criteria = [];
         addCriteria();
      }

      if (queryFilter.criteria.length == 0)
      {
         /////////////////////////////////////////////
         // make sure there's at least one criteria //
         /////////////////////////////////////////////
         addCriteria();
      }
      else
      {
         ////////////////////////////////////////////////////////////////////////////////////
         // make sure all criteria have an id on them (to be used as react component keys) //
         //                                                                                //
         // deliberately NOT followed by an updateFilter call.  the ids are assigned onto  //
         // the caller's own criterion objects, so they stick without one - and this runs  //
         // in the render body, where updateFilter reaches into RecordQuery's              //
         // doSetQueryFilter: a state update on another component mid-render, which also   //
         // resets the page number, writes the view to local storage and (in picker usage) //
         // clears the checkboxes the user had already ticked.  Criteria from quick        //
         // filters and from a rehydrated view carry no id, so that would fire the first   //
         // time the Filter Builder is opened on any existing filter.                      //
         ////////////////////////////////////////////////////////////////////////////////////
         for (let i = 0; i < queryFilter.criteria.length; i++)
         {
            const criteria = queryFilter.criteria[i] as QFilterCriteriaWithId;
            if (!criteria.id)
            {
               criteria.id = criteriaId++;
            }
         }
      }

      if (queryFilter.criteria.length == 1 && !queryFilter.criteria[0].fieldName)
      {
         focusLastField();
      }

      let booleanOperator: "AND" | "OR" | null = null;
      if (queryFilter.criteria.length > 1)
      {
         booleanOperator = queryFilter.booleanOperator;
      }

      //////////////////////////////////////////////////////////////////////////////////
      // with criteria locked, the And that RecordQuery enforces on the query is what //
      // this (disabled) control has to show.  The filter keeps its own operator, so  //
      // reading it straight would put an Or on screen beside a caption saying And is //
      // fixed.  Display only - nothing is written back into the filter.              //
      //////////////////////////////////////////////////////////////////////////////////
      if (booleanOperator != null && (props.lockedCriteriaCount ?? 0) > 0)
      {
         booleanOperator = "AND";
      }

      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // needDebounce param - things like typing in a text field DO need debounce, but changing an operator doesn't //
      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      const updateCriteria = (newCriteria: QFilterCriteria, index: number, needDebounce = false) =>
      {
         queryFilter.criteria[index] = newCriteria;

         clearTimeout(debounceTimeout);
         debounceTimeout = setTimeout(() => props.updateFilter(queryFilter), needDebounce ? 500 : 1);

         forceUpdate();
      };

      const updateBooleanOperator = (newValue: string) =>
      {
         queryFilter.booleanOperator = newValue as "AND" | "OR";
         props.updateFilter(queryFilter);
         forceUpdate();
      };

      const removeCriteria = (index: number) =>
      {
         ////////////////////////////////////////////////////////////////////////////
         // belt-and-braces - a locked row renders no remove control at all, but a //
         // locked criterion must never be removable through this path either.     //
         ////////////////////////////////////////////////////////////////////////////
         if (index < (props.lockedCriteriaCount ?? 0))
         {
            return;
         }

         queryFilter.criteria.splice(index, 1);
         props.updateFilter(queryFilter);
         forceUpdate();
      };

      const lockedCriteriaCount = props.lockedCriteriaCount ?? 0;

      return (
         <Box className="customFilterPanel">
            {
               ////////////////////////////////////////////////////////////////////////////////////
               // say why the leading rows are read-only and why And/Or is disabled.  Without    //
               // this the panel just shows controls that do not respond, and a filter that came //
               // in as Or looks as though it had always been an And.                            //
               //                                                                                //
               // The And/Or half is said only when there IS an And/Or control on screen: it     //
               // renders between criteria, so a single locked condition would otherwise be      //
               // told a control it cannot see is fixed.                                         //
               //                                                                                //
               // And when the filter really does say Or, the caption says so AND says that      //
               // saving stores it as And.  That is the whole point of the sentence: the And is  //
               // going to be written to the record, and a user who is told first is not having  //
               // their filter changed behind them - which is exactly what the old behaviour     //
               // did, rewriting the operator on load so that merely opening this editor and     //
               // clicking OK silently changed what a report returned.                           //
               ////////////////////////////////////////////////////////////////////////////////////
               lockedCriteriaCount > 0 &&
               <Box px={1} pt={1} pb={0.5}>
                  <MDTypography variant="caption" color="text" fontWeight="regular">
                     {lockedCriteriaCount == 1 ? "The first condition is" : `The first ${lockedCriteriaCount} conditions are`} required here and cannot be changed or removed.
                     {
                        booleanOperator != null &&
                        <>&nbsp;And/Or is fixed at And, because an Or would let a record match without satisfying {lockedCriteriaCount == 1 ? "it" : "them"}.</>
                     }
                     {
                        props.lockedCriteriaIsOverridingBooleanOperator &&
                        <>&nbsp;This filter is stored as Or - it is being treated as And while these conditions are locked, and saving here will store it as And.</>
                     }
                  </MDTypography>
               </Box>
            }
            {
               (queryFilter.criteria as QFilterCriteriaWithId[]).map((criteria: QFilterCriteriaWithId, index: number) =>
                  (
                     <Box key={criteria.id}>
                        <FilterCriteriaRow
                           id={criteria.id}
                           index={index}
                           tableMetaData={props.tableMetaData}
                           metaData={props.metaData}
                           criteria={criteria}
                           booleanOperator={booleanOperator}
                           updateCriteria={(newCriteria, needDebounce) => updateCriteria(newCriteria, index, needDebounce)}
                           removeCriteria={() => removeCriteria(index)}
                           updateBooleanOperator={(newValue) => updateBooleanOperator(newValue)}
                           allowVariables={props.allowVariables}
                           queryScreenUsage={props.queryScreenUsage}
                           omitExposedJoins={props.omitExposedJoins}
                           isLocked={index < (props.lockedCriteriaCount ?? 0)}
                           booleanOperatorIsLocked={(props.lockedCriteriaCount ?? 0) > 0}
                        />
                        {/*JSON.stringify(criteria)*/}
                     </Box>
                  ))
            }
            <Box p={1}>
               <Button onClick={() => addCriteria()} startIcon={<Icon>add</Icon>} size="medium" sx={{px: 0.75}}>Add Condition</Button>
            </Box>
         </Box>
      );
   }
);
